import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "./config/api";
import Navbar from "./components/Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot, faClock, faUsers, faUser, faWifi, faCalendar,
  faChevronDown, faChevronUp, faChevronRight, faChevronLeft, faStar,
  faPlus, faMinus, faChild
} from "@fortawesome/free-solid-svg-icons";
import Footer from "./components/Footer";
// import FloatingImage from "./assests/AIicon.png";
import FloatingAIButton from "./components/FloatingAIButton";
import Modal from "./Modal";

function HotelDetails() {
  const { hotel, hotelId, itineraryDetails, dayIndex } = useLocation().state || {};
  const navigate = useNavigate();
  const [roomType, setRoomType] = useState(hotel?.roomType || "Standard");
  const [nights, setNights] = useState(hotel?.nights || 1);
  const [hotelDetails, setHotelDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentIndexes, setCurrentIndexes] = useState([0]);

  // New state variables for date selection and room occupancy
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [showRoomOccupancy, setShowRoomOccupancy] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([
    { id: 1, adults: 2, children: 0 }
  ]);

  const overviewRef = useRef(null);
  const roomRef = useRef(null);
  const photosRef = useRef(null);
  const locationRef = useRef(null);
  const reviewsRef = useRef(null);

  const [overviewExpanded, setOverviewExpanded] = useState(true);
  const [roomExpanded, setRoomExpanded] = useState(true);
  const [locationExpanded, setLocationExpanded] = useState(true);
  const [photoExpanded, setPhotoExpanded] = useState(true);
  const [reviewExpanded, setReviewExpanded] = useState(true);

  const itineraryData = [
    { hotel: { images: ["image1_url", "image2_url"] } }, // Example data, replace with actual data
  ];

  useEffect(() => {
    if (hotelId) {
      const fetchHotelDetails = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/hotels/${hotelId}`);
          if (!response.ok) throw new Error("Failed to fetch hotel details.");
          const result = await response.json();
          setHotelDetails(result);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchHotelDetails();
      const interval = setInterval(fetchHotelDetails, 3000);
      return () => clearInterval(interval);
    }
  }, [hotelId]);

  const fetchUpdatedPrice = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hotels/calculateHotelPrice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hotelId: hotelDetails._id,
          roomType: selectedRoom.roomType,
          mealPlan: "CPAI", // adjust if needed
          occupancy: rooms,  // your occupancy array
          nights: nights
        })
      });
      if (!response.ok) throw new Error("Price calculation failed");
      const data = await response.json();
      return data.totalPrice;
    } catch (error) {
      console.error("Error calculating price:", error);
      alert("Error calculating hotel price.");
      return null;
    }
  };

  // Helper to recalc the package's overall total price by summing hotels’ totalPrice on every day
  const recalcPackageTotal = (updatedPackage) => {
    if (!updatedPackage || !updatedPackage.itinerary) return 0;

    return updatedPackage.itinerary.reduce((total, day) => {
      if (day.hotels && Array.isArray(day.hotels)) {
        const dayTotal = day.hotels.reduce((daySum, hotel) =>
          daySum + (hotel.totalPrice || 0), 0);
        return total + dayTotal;
      }
      return total;
    }, 0);
  };

  // Updated handleUpdatePackage to update the hotel details and recalc the overall package total price.
  const handleUpdatePackage = async (selectedRoomInfo, newTotalPrice) => {
    if (!itineraryDetails) return alert("Missing itinerary details");
    if (!itineraryDetails.itinerary || !Array.isArray(itineraryDetails.itinerary)) {
      return alert("Invalid itinerary data");
    }
    if (dayIndex === undefined || dayIndex < 0 || dayIndex >= itineraryDetails.itinerary.length) {
      return alert("Invalid day index");
    }

    if (!checkInDate || !checkOutDate) {
      return alert("Please select check-in and check-out dates");
    }
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    if (checkIn >= checkOut) {
      return alert("Check-out date must be after check-in date");
    }

    // Clone itineraryDetails to avoid mutations
    const updatedPackage = JSON.parse(JSON.stringify(itineraryDetails));

    // Clean-up any nested activityId objects in the itinerary
    updatedPackage.itinerary = updatedPackage.itinerary.map((day) => {
      if (day.destinations && Array.isArray(day.destinations)) {
        day.destinations = day.destinations.map((destination) => {
          if (destination.activities && Array.isArray(destination.activities)) {
            destination.activities = destination.activities.map((activity) => {
              if (activity.activityId && typeof activity.activityId === "object") {
                if (activity.activityId._id) {
                  activity.activityId = activity.activityId._id;
                } else if (activity.activityId.id) {
                  activity.activityId = activity.activityId.id;
                } else {
                  delete activity.activityId;
                }
              }
              return activity;
            });
          }
          return destination;
        });
      }
      return day;
    });

    // Fix any top-level activityId if present
    if (updatedPackage.activityId && typeof updatedPackage.activityId === "object") {
      if (updatedPackage.activityId._id) {
        updatedPackage.activityId = updatedPackage.activityId._id;
      } else if (updatedPackage.activityId.id) {
        updatedPackage.activityId = updatedPackage.activityId.id;
      } else {
        delete updatedPackage.activityId;
      }
    }

    // Ensure hotels array exists for the given day
    if (!updatedPackage.itinerary[dayIndex].hotels) {
      updatedPackage.itinerary[dayIndex].hotels = [];
    }

    // Update or add the hotel details for that day
    if (updatedPackage.itinerary[dayIndex].hotels.length > 0) {
      updatedPackage.itinerary[dayIndex].hotels = updatedPackage.itinerary[dayIndex].hotels.map(hotel => ({
        ...hotel,
        hotelId: selectedRoomInfo.hotelId,
        roomType: selectedRoomInfo.roomType,
        checkInDate,
        checkOutDate,
        rooms: rooms,
        totalPrice: newTotalPrice
      }));
    } else {
      updatedPackage.itinerary[dayIndex].hotels.push({
        hotelId: selectedRoomInfo.hotelId,
        roomType: selectedRoomInfo.roomType,
        checkInDate,
        checkOutDate,
        rooms: rooms,
        totalPrice: newTotalPrice
      });
    }

    // Recalculate the overall package total price based on all days’ hotels.
    updatedPackage.totalPrice = recalcPackageTotal(updatedPackage);

    try {
      const response = await fetch(`${API_BASE_URL}/packages/${itineraryDetails._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedPackage)
      });
      if (response.ok) {
        const result = await response.json();
        alert("Hotel updated successfully!");
        navigate(`/tripdetails?id=${itineraryDetails._id}`);
      } else {
        const errorText = await response.text();
        console.error("Server error:", errorText);
        alert(`Error updating package: ${errorText}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error updating package");
    }
  };

  const handleConfirmBooking = async () => {
    if (selectedRoom) {
      // First, calculate the updated price based on current occupancy & nights.
      const newTotalPrice = await fetchUpdatedPrice();
      if (newTotalPrice === null) return;
      // Now update the package with the new price included.
      handleUpdatePackage(selectedRoom, newTotalPrice);
    } else {
      alert("Please select a room first");
    }
  };

  const handleRoomSelection = (selectedRoom) => {
    setRoomType(selectedRoom.roomType);
    setSelectedRoom(selectedRoom);
    setShowRoomOccupancy(true);
  };

  const handleSwipe = (dayIndex, direction) => {
    setCurrentIndexes(prev => {
      const newIndex = [...prev];
      const totalImages = itineraryData[dayIndex].hotel.images.length;
      newIndex[dayIndex] = (newIndex[dayIndex] + (direction === "left" ? -1 : 1) + totalImages) % totalImages;
      return newIndex;
    });
  };

  const handleScrollToSection = (ref) => ref.current.scrollIntoView({ behavior: "smooth" });

  const handleToggle = () => setOverviewExpanded(!overviewExpanded);
  const handleToggleRoom = () => setRoomExpanded(!roomExpanded);
  const handleToggleLocation = () => setLocationExpanded(!locationExpanded);
  const handleTogglePhoto = () => setPhotoExpanded(!photoExpanded);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // Functions to handle room occupancy changes
  const addRoom = () => {
    setRooms([...rooms, { id: rooms.length + 1, adults: 2, children: 0 }]);
  };

  const removeRoom = (roomId) => {
    if (rooms.length > 1) {
      setRooms(rooms.filter(room => room.id !== roomId));
    }
  };

  const updateRoomOccupancy = (roomId, field, value) => {
    setRooms(rooms.map(room => {
      if (room.id === roomId) {
        const updatedRoom = { ...room, [field]: value };

        // Apply constraints: maximum 3 adults + 1 child per room
        if (field === 'adults' && value > 3) {
          updatedRoom.adults = 3;
        }
        if (field === 'children' && value > 1) {
          updatedRoom.children = 1;
        }
        if (field === 'adults' && value < 1) {
          updatedRoom.adults = 1;
        }
        if (field === 'children' && value < 0) {
          updatedRoom.children = 0;
        }

        return updatedRoom;
      }
      return room;
    }));
  };

  if (loading) return <div>Loading hotel details...</div>;
  if (error) return <div>{error}</div>;
  if (!hotelDetails) return <div>No hotel details available.</div>;
  const { name, rating, amenities, price, images, seasonalRates, contactEmail, contactNumbers, website, state, isHoneymoonFavorite } = hotelDetails;

  return (
    <>
      <Navbar />
      <div style={styles.destinationPage}>
        <header style={styles.destinationHeader}>
          <div style={styles.leftContent}>
            <h1 style={styles.h1}>Hotel Detail</h1>
          </div>
          <img
            src="https://img.freepik.com/free-vector/hotel-reception-concept-illustration_114360-14045.jpg?uid=R180763786&ga=GA1.1.1046031348.1735292585&semt=ais_hybrid"
            alt="hotel image"
            style={{ width: 1350, height: 300, objectFit: "cover", objectPosition: "center", position: "relative" }}
          />
        </header>

        <div style={styles.cityPhotoContainer}>
          <img style={styles.cityPhoto} src={images[0]} alt="Hotel" />
          <div style={styles.buttonsContainer}>
            <button style={styles.smallButton}>Photos</button>
            <button style={styles.smallButton}>Videos</button>
          </div>
        </div>

        <div style={styles.container}>
          <main style={styles.mainContent}>
            <div style={styles.tabs}>
              {["Overview", "Rooms", "Location", "Photos"].map((text, index) => (
                <button
                  key={index}
                  style={{ ...styles.tabButton, borderBottom: "2px solid #9A9A9A" }}
                  onClick={() => handleScrollToSection([overviewRef, roomRef, locationRef, photosRef][index])}
                >
                  {text}
                </button>
              ))}
              <div style={{ marginTop: "-20px" }}>
                <p style={{ color: "#02495B", fontSize: "28px", fontFamily: "Merriweather", fontWeight: "700", textAlign: "right" }}>
                  Rs {price}/- Per Night
                </p>
                <p style={{ color: "#4A4A4A", fontSize: "16px", fontFamily: "Merriweather", fontWeight: "400", textAlign: "right", marginTop: -20 }}>
                  for 1 Adult
                </p>
              </div>
            </div>

            <section ref={overviewRef} style={styles.section}>
              <h1 style={styles.mainHeading}>{name}</h1>
              <div style={styles.reviewContainer}>
                <span style={styles.stars}>{Array.from({ length: rating }, () => "⭐").join("")}</span>
                {isHoneymoonFavorite && (
                  <span style={styles.honeymoonBadge}>Honeymoon Favorite</span>
                )}
              </div>
              <div style={styles.infoRow}>
                {[{ icon: faClock, text: "356 Days" }, { icon: faWifi, text: amenities.join(", ") }, { icon: faLocationDot, text: state.name }]
                  .map(({ icon, text }, index) => (
                    <div key={index} style={styles.infoItem}>
                      <FontAwesomeIcon icon={icon} style={{ color: "#1CA8CB", ...styles.icon }} /> {text}
                    </div>
                  ))}
              </div>
              <hr style={styles.divider} />
              <h2 style={{ ...styles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Overview
                <FontAwesomeIcon icon={overviewExpanded ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} onClick={handleToggle} />
              </h2>
              {overviewExpanded && (
                <p style={styles.sectionText}>
                  Welcome to <strong>{name}</strong>, located in the beautiful state of <strong>{state.name}</strong>. This resort offers a perfect getaway with its stunning beach access and luxurious amenities. Enjoy a comfortable stay in one of our well-appointed rooms, including the{" "}
                  <strong>{seasonalRates[0].rates[0].roomType}</strong> with breathtaking views. Whether you're visiting for a romantic escape or a family vacation, we provide everything you need for a memorable experience:
                  <ul>{amenities.map((amenity, index) => <li key={index}>{amenity}</li>)}</ul>
                  During the <strong>{seasonalRates[0].season}</strong> season (from{" "}
                  {new Date(seasonalRates[0].startDate).toLocaleDateString()} to{" "}
                  {new Date(seasonalRates[0].endDate).toLocaleDateString()}), enjoy exclusive room rates and honeymoon inclusions, such as a private beach dinner and flower bed decoration. For more information or to book your stay, you can visit our website at{" "}
                  <a href={website} target="_blank" rel="noopener noreferrer">{name}</a> or contact us at <strong>{contactEmail}</strong> or <strong>{contactNumbers[0]}</strong>.
                </p>
              )}
            </section>

            <hr style={styles.divider} />
            <h2 style={{ ...styles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Rooms
              <FontAwesomeIcon icon={roomExpanded ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} onClick={handleToggleRoom} />
            </h2>
            {roomExpanded && (
              <div style={styles.roomListContainer}>
                <div style={styles.roomList} ref={roomRef}>
                  {seasonalRates.map((season) => (
                    <div key={season._id} style={styles.seasonCard}>
                      <h3 style={styles.seasonTitle}>{season.season}</h3>
                      <div style={styles.seasonInfo}>
                        <p style={styles.seasonDetails}><strong>Season Dates: </strong>{new Date(season.startDate).toLocaleDateString()} - {new Date(season.endDate).toLocaleDateString()}</p>
                        <div style={styles.roomList}>
                          {season.rates.map((room) => (
                            <div key={room._id} style={styles.roomCard}>
                              <img src={room.images[0]} alt={room.roomType} style={styles.roomImg} />
                              <h3 style={styles.roomType}>{room.roomType}</h3>
                              <p style={styles.roomPrice}>Rs. {room.specialRateCPAI} / night</p>
                              <p style={styles.roomDescription}>Rack Rate: Rs. {room.rackRate}, APAI: Rs. {room.specialRateAPAI}</p>
                              <p style={styles.roomDescription}>Extra Person Rate: Rs. {room.extraPersonRate}, Extra Child Rate: Rs. {room.extraChildRate}</p>
                              {season.honeymoonInclusions && <p style={styles.honeymoonText}><strong>Honeymoon Inclusions: </strong>{season.honeymoonInclusions}</p>}
                              <button style={styles.bookButton} onClick={() => handleRoomSelection({ hotelId: hotelDetails._id, roomType: room.roomType })}>Select</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <hr style={styles.divider} />
            <div style={styles.section} ref={locationRef}>
              <h2 style={{ ...styles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Location
                <FontAwesomeIcon icon={locationExpanded ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} onClick={handleToggleLocation} />
              </h2>
              {locationExpanded && <p style={styles.address}>{state.name}</p>}
            </div>

            <hr style={styles.divider} />
            <div style={styles.section} ref={photosRef}>
              <h2 style={{ ...styles.sectionHeading, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Photos
                <FontAwesomeIcon icon={photoExpanded ? faChevronUp : faChevronDown} style={{ cursor: "pointer" }} onClick={handleTogglePhoto} />
              </h2>
              {photoExpanded && (
                <div>
                  {itineraryData.map((dayData, dayIdx) => (
                    <div key={dayIdx}>
                      <div style={{ ...styles.swipeableContainer, display: "flex", alignItems: "center" }}>
                        <button
                          style={{ backgroundColor: "white", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #ccc", cursor: "pointer", boxShadow: "0px 2px 5px rgba(0,0,0,0.2)" }}
                          onClick={() => handleSwipe(dayIdx, "left")}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} style={{ color: "#008F82" }} />
                        </button>
                        <div style={{ display: "flex", gap: "10px" }}>
                          {images.map((img, index) => <img key={index} src={img} alt={`Hotel ${index + 1}`} style={{ width: "300px", margin: "10px" }} />)}
                        </div>
                        <button
                          style={{ backgroundColor: "white", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #ccc", cursor: "pointer", boxShadow: "0px 2px 5px rgba(0,0,0,0.2)" }}
                          onClick={() => handleSwipe(dayIdx, "right")}
                        >
                          <FontAwesomeIcon icon={faChevronRight} style={{ color: "#008F82" }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Date Selection and Room Occupancy Modal */}
      {showRoomOccupancy && (
        <div style={styles.modalOverlay}>
          <div style={styles.dateRoomModal}>
            <h2 style={styles.modalTitle}>Complete Your Booking</h2>

            <div style={styles.dateSelectionContainer}>
              <div style={styles.dateInput}>
                <label style={styles.dateLabel}>
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: "8px" }} />
                  Check-in Date:
                </label>
                <input
                  type="date"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e.target.value)}
                  style={styles.dateInputField}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div style={styles.dateInput}>
                <label style={styles.dateLabel}>
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: "8px" }} />
                  Check-out Date:
                </label>
                <input
                  type="date"
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  style={styles.dateInputField}
                  min={checkInDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <h3 style={styles.roomOccupancyTitle}>Room Occupancy</h3>

            {rooms.map((room, index) => (
              <div key={room.id} style={styles.roomOccupancyCard}>
                <div style={styles.roomHeader}>
                  <h4 style={styles.roomTitle}>Room {index + 1}</h4>
                  {rooms.length > 1 && (
                    <button
                      onClick={() => removeRoom(room.id)}
                      style={styles.removeRoomButton}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div style={styles.occupancyControls}>
                  <div style={styles.occupancyControl}>
                    <span style={styles.occupancyLabel}>
                      <FontAwesomeIcon icon={faUser} style={{ marginRight: "8px" }} />
                      Adults:
                    </span>
                    <div style={styles.counterControl}>
                      <button
                        onClick={() => updateRoomOccupancy(room.id, 'adults', room.adults - 1)}
                        style={styles.counterButton}
                        disabled={room.adults <= 1}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span style={styles.counterValue}>{room.adults}</span>
                      <button
                        onClick={() => updateRoomOccupancy(room.id, 'adults', room.adults + 1)}
                        style={styles.counterButton}
                        disabled={room.adults >= 3}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    <span style={styles.maxCapacity}>(Max: 3)</span>
                  </div>

                  <div style={styles.occupancyControl}>
                    <span style={styles.occupancyLabel}>
                      <FontAwesomeIcon icon={faChild} style={{ marginRight: "8px" }} />
                      Children:
                    </span>
                    <div style={styles.counterControl}>
                      <button
                        onClick={() => updateRoomOccupancy(room.id, 'children', room.children - 1)}
                        style={styles.counterButton}
                        disabled={room.children <= 0}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span style={styles.counterValue}>{room.children}</span>
                      <button
                        onClick={() => updateRoomOccupancy(room.id, 'children', room.children + 1)}
                        style={styles.counterButton}
                        disabled={room.children >= 1}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    <span style={styles.maxCapacity}>(Max: 1, Age: 0-12)</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Show "Add Room" button only if total occupancy requires it */}
            <button
              onClick={addRoom}
              style={styles.addRoomButton}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: "8px" }} />
              Add Another Room
            </button>

            <div style={styles.modalActions}>
              <button
                onClick={() => setShowRoomOccupancy(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBooking}
                style={styles.confirmButton}
                disabled={!checkInDate || !checkOutDate}
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <FloatingAIButton onClick={openModal} />
      {/* Modal */}
      {isModalOpen && <Modal closeModal={closeModal} />}
    </>
  );
}

const styles = {
  container: { display: "flex", padding: "20px", fontFamily: "Arial, sans-serif" },
  destinationPage: { padding: "20px", fontFamily: "Arial, sans-serif" },
  destinationHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", marginBottom: "20px" },
  leftContent: { position: "absolute", left: "100px", top: "50%", transform: "translateY(-50%)", zIndex: 2, color: "white" },
  h1: { fontFamily: '"Merriweather", serif', fontSize: "70px", fontWeight: 400, marginTop: "-30px", marginLeft: "-40px", color: "#004e61" },
  breadcrumb: { fontSize: "27px", fontWeight: 300, fontFamily: '"Merriweather", serif', color: "#1ca8cb", marginTop: "5%" },
  breadcrumbLink: { textDecoration: "none", color: "#1ca8cb" },
  rightImage: { width: "96vw", height: "300px", objectFit: "cover", filter: "brightness(50%)", position: "relative" },
  cityPhotoContainer: { position: "relative", width: "fit-content" },
  cityPhoto: { width: "96vw", height: "450px", objectFit: "cover", filter: "brightness(80%)", position: "relative" },
  buttonsContainer: { position: "absolute", bottom: "30px", left: "40px", display: "flex", gap: "20px" },
  smallButton: { padding: "10px 20px", fontSize: "15px", fontWeight: "700", fontFamily: "Merriweather", backgroundColor: "#1CA8CB", color: "white", border: "none", borderRadius: "26px", cursor: "pointer" },
  mainContent: { width: "100%", padding: "0 20px" },
  tabs: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  tabButton: { flex: 1, padding: "10px 20px", border: "none", borderBottom: "2px solid transparent", background: "none", fontSize: "17px", fontWeight: "700", cursor: "pointer", color: "#1CA8CB", fontFamily: "Merriweather" },
  section: { marginTop: "30px" },
  mainHeading: { fontSize: "35px", fontWeight: "700", marginBottom: "10px", color: "#004E61", fontFamily: "Merriweather", lineHeight: "50px" },
  reviewContainer: { display: "flex", alignItems: "center", fontSize: "17px", marginBottom: "20px", color: "#535353", fontFamily: "Merriweather", fontWeight: "300" },
  stars: { color: "#FFA944", marginRight: "10px" },
  honeymoonBadge: {
    backgroundColor: "#E6E6FA", // Soft lavender
    color: "#4B0082", // Indigo for contrast
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    marginLeft: "10px",
    fontWeight: "bold",
    display: "inline-block", // Ensures it behaves like a pill
  },
  infoRow: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", marginBottom: "10px" },
  infoItem: { display: "flex", alignItems: "center", fontSize: "17px", color: "#535353", fontWeight: "400", fontFamily: "Merriweather", lineHeight: "28px", marginRight: "20px", marginBottom: "10px" },
  "@media (max-width: 1200px)": { infoItem: { fontSize: "16px", marginRight: "15px" } },
  "@media (max-width: 768px)": { infoRow: { flexDirection: "column", alignItems: "flex-start" }, infoItem: { fontSize: "15px", marginRight: "0", marginBottom: "8px" } },
  "@media (max-width: 480px)": { infoItem: { fontSize: "14px", lineHeight: "24px" } },
  icon: { marginRight: "10px" },
  divider: { margin: "20px 0", border: "none", borderTop: "1px solid #ddd" },
  sectionHeading: { fontFamily: "Merriweather", fontSize: "25px", fontWeight: "700", marginBottom: "10px", color: "#004E61", lineHeight: "50px" },
  sectionText: { fontFamily: "Merriweather", color: "#4A4A4A", fontWeight: "300", fontSize: "17px", lineHeight: "28px" },
  roomListContainer: { position: "relative" },
  roomList: { display: "flex", overflowX: "scroll", padding: "10px 0", scrollSnapType: "x mandatory", scrollbarWidth: "none", "-ms-overflow-style": "none" },
  roomCard: { width: "350px", marginRight: "15px", backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #ddd", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", padding: "12px", scrollSnapAlign: "start", flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" },
  roomImg: { width: "100%", height: "180px", objectFit: "cover", borderRadius: "8px", marginBottom: "8px" },
  roomType: { fontFamily: "Merriweather", fontSize: "18px", fontWeight: "600", color: "#333", marginBottom: "5px" },
  roomPrice: { fontFamily: "Merriweather", fontSize: "16px", fontWeight: "500", color: "#1CA8CB", marginBottom: "10px" },
  roomDescription: { fontFamily: "Arial", fontSize: "14px", color: "#666", marginBottom: "10px", wordWrap: "break-word" },
  seasonInfo: { fontFamily: "Arial", fontSize: "12px", color: "#666", marginBottom: "10px", wordWrap: "break-word" },
  seasonDetails: { marginBottom: "5px" },
  honeymoonText: { marginTop: "5px" },
  bookButton: { backgroundColor: "#02495B", color: "#fff", padding: "10px 20px", borderRadius: "25px", border: "none", cursor: "pointer", fontSize: "16px", width: "150px" },
  address: { fontFamily: "Merriweather", fontSize: "18px", fontWeight: "400", color: "#4A4A4A", marginTop: "10px" },
  list: { listStyleType: "none", paddingLeft: "0" },
  swipeableContainer: { display: "flex", transition: "transform 0.5s ease" },
  hotelImage: { width: "201px", height: "133px", margin: "0 5px" },
  reviewsContainer: { display: "flex", flexWrap: "wrap", justifyContent: "space-between" },
  userReview: { display: "flex", alignItems: "center", marginBottom: "10px" },
  userAvatar: { width: "66px", height: "66px", borderRadius: "50%", backgroundColor: "#D9D9D9", marginRight: "10px" },
  userName: { fontFamily: "Merriweather", fontSize: "23px", fontWeight: "700", lineHeight: "35px", textAlign: "justify", color: "#1CA8CB" },
  userStars: { display: "flex", marginTop: "5px" },
  starFilled: { color: "#FFD700", marginRight: "3px" },
  grayStar: { color: "#D9D9D9", marginRight: "3px" },
  userComment: { fontFamily: "Merriweather", fontSize: "13px", fontWeight: "300", lineHeight: "30px", textAlign: "justify", marginTop: "5px", color: "#4A4A4A" },
  formSection: { marginTop: "20px" },
  formHeading: { fontFamily: "Merriweather", fontSize: "23px", fontWeight: "700", lineHeight: "35px", textAlign: "justify", color: "#1CA8CB" },
  inlineFormGroup: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
  formControlWrapper: { flex: "0 0 48%" },
  formGroup: { marginBottom: "15px" },
  formControl: { width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #1CA8CB" },
  buttonWrapper: { display: "flex", justifyContent: "center" },
  submitButton: { backgroundColor: "#113D48", color: "#fff", padding: "1px 30px", border: "none", borderRadius: "30px", cursor: "pointer", fontFamily: "Merriweather", fontSize: "18px", fontWeight: "400", lineHeight: "50px" },
  floatingBox: { position: "fixed", bottom: "20px", right: "115px", width: "96px", height: "96px", display: "flex", borderRadius: "15px", justifyContent: "center", alignItems: "center", zIndex: 10 },
  floatingButton: { border: "none", backgroundColor: "transparent", padding: 0, cursor: "pointer", borderRadius: "15px" },
  floatingImage: { width: "96px", height: "96px", objectFit: "cover", borderRadius: "15px" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.7)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  dateRoomModal: { backgroundColor: "white", borderRadius: "8px", padding: "24px", width: "90%", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto" },
  modalTitle: { color: "#02495B", fontSize: "24px", marginBottom: "20px", textAlign: "center" },
  dateSelectionContainer: { display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" },
  dateInput: { display: "flex", flexDirection: "column", gap: "8px" },
  dateLabel: { fontSize: "16px", color: "#4A4A4A", fontWeight: 500 },
  dateInputField: { padding: "10px", fontSize: "16px", borderRadius: "4px", border: "1px solid #ccc" },
  roomOccupancyTitle: { fontSize: "18px", color: "#02495B", marginBottom: "16px" },
  roomOccupancyCard: { border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", marginBottom: "16px", backgroundColor: "#f9f9f9" },
  roomHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  roomTitle: { fontSize: "16px", margin: 0, color: "#02495B" },
  removeRoomButton: { backgroundColor: "#f44336", color: "white", border: "none", borderRadius: "4px", padding: "6px 12px", cursor: "pointer", fontSize: "14px" },
  occupancyControls: { display: "flex", flexDirection: "column", gap: "16px" },
  occupancyControl: { display: "flex", alignItems: "center" },
  occupancyLabel: { width: "100px", fontSize: "14px", color: "#4A4A4A" },
  counterControl: { display: "flex", alignItems: "center", marginRight: "12px" },
  counterButton: { width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#e0e0e0", border: "none", borderRadius: "4px", cursor: "pointer" },
  counterValue: { margin: "0 12px", fontSize: "16px", fontWeight: 500 },
  maxCapacity: { fontSize: "12px", color: "#666" },
  addRoomButton: { backgroundColor: "#02495B", color: "white", border: "none", borderRadius: "4px", padding: "10px 16px", cursor: "pointer", fontSize: "14px", marginBottom: "24px", width: "100%" },
  modalActions: { display: "flex", justifyContent: "space-between", gap: "16px", marginTop: "24px" },
  cancelButton: { flex: 1, padding: "12px", fontSize: "16px", backgroundColor: "#e0e0e0", border: "none", borderRadius: "4px", cursor: "pointer" },
  confirmButton: { flex: 1, padding: "12px", fontSize: "16px", backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
};

export default HotelDetails;
